import { Link } from "react-router-dom";
import { BookOpen, ArrowLeft, Search, FileText, Code, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

export default function DocsPage() {
    return (
        <div className="min-h-screen bg-background text-foreground">
            {/* Header */}
            <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
                <div className="container flex h-14 max-w-screen-2xl items-center">
                    <div className="mr-4 flex">
                        <Link to="/" className="mr-6 flex items-center space-x-2">
                            <span className="font-bold sm:inline-block">Braise Docs</span>
                        </Link>
                    </div>
                    <div className="flex flex-1 items-center justify-between space-x-2 md:justify-end">
                        <div className="w-full flex-1 md:w-auto md:flex-none">
                            <div className="relative">
                                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                                <Input placeholder="Search documentation..." className="pl-8 md:w-[300px] lg:w-[400px]" />
                            </div>
                        </div>
                        <nav className="flex items-center">
                            <Link to="/">
                                <Button variant="ghost" size="sm">
                                    <ArrowLeft className="mr-2 h-4 w-4" />
                                    Back to App
                                </Button>
                            </Link>
                        </nav>
                    </div>
                </div>
            </header>

            <main className="container max-w-screen-2xl py-12">
                <div className="mx-auto max-w-4xl space-y-12">
                    {/* Hero Section */}
                    <div className="text-center space-y-4">
                        <Badge variant="secondary" className="mb-4">v2.0.0 Documentation</Badge>
                        <h1 className="text-4xl font-extrabold tracking-tight lg:text-5xl">
                            Build faster with Braise
                        </h1>
                        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                            Everything you need to integrate, customize, and extend the Braise platform for your restaurant.
                        </p>
                    </div>

                    {/* Quick Links Grid */}
                    <div className="grid gap-6 md:grid-cols-3">
                        <Card className="hover:border-primary/50 transition-colors cursor-pointer group">
                            <CardHeader>
                                <div className="p-2 w-fit rounded-lg bg-primary/10 text-primary mb-2 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                                    <Zap className="h-6 w-6" />
                                </div>
                                <CardTitle>Quick Start</CardTitle>
                                <CardDescription>Get up and running in less than 5 minutes.</CardDescription>
                            </CardHeader>
                        </Card>
                        <Card className="hover:border-primary/50 transition-colors cursor-pointer group">
                            <CardHeader>
                                <div className="p-2 w-fit rounded-lg bg-primary/10 text-primary mb-2 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                                    <Code className="h-6 w-6" />
                                </div>
                                <CardTitle>API Reference</CardTitle>
                                <CardDescription>Detailed endpoints and object structures.</CardDescription>
                            </CardHeader>
                        </Card>
                        <Card className="hover:border-primary/50 transition-colors cursor-pointer group">
                            <CardHeader>
                                <div className="p-2 w-fit rounded-lg bg-primary/10 text-primary mb-2 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                                    <FileText className="h-6 w-6" />
                                </div>
                                <CardTitle>Guides</CardTitle>
                                <CardDescription>Step-by-step tutorials for common tasks.</CardDescription>
                            </CardHeader>
                        </Card>
                    </div>

                    {/* Coming Soon State */}
                    <div className="rounded-xl border border-dashed border-border p-8 text-center">
                        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                            <BookOpen className="h-6 w-6 text-muted-foreground" />
                        </div>
                        <h3 className="mt-4 text-lg font-semibold">Full Documentation Coming Soon</h3>
                        <p className="mt-2 text-sm text-muted-foreground">
                            We're currently rewriting our documentation to be more comprehensive. <br />
                            In the meantime, please contact support for assistance.
                        </p>
                        <div className="mt-6">
                            <Button>Contact Support</Button>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}
